import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { financeService, type PurchaseCategory } from '@/services/finance.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues {
  name: string;
  monthly_budget: string;
  max_purchase_limit: string;
  gst_applicable: boolean;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  category?: PurchaseCategory;
  onClose: () => void;
  onSuccess: () => void;
}

export function CategoryFormDialog({ open, mode, category, onClose, onSuccess }: Props) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: { name: '', monthly_budget: '', max_purchase_limit: '', gst_applicable: false },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: category?.name ?? '',
        monthly_budget: category ? String(Number(category.monthly_budget)) : '',
        max_purchase_limit: category ? String(Number(category.max_purchase_limit)) : '',
        gst_applicable: category?.gst_applicable ?? false,
      });
    }
  }, [open, category, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const input = {
        name: values.name.trim(),
        monthly_budget: Number(values.monthly_budget) || 0,
        max_purchase_limit: Number(values.max_purchase_limit) || 0,
        gst_applicable: values.gst_applicable,
      };
      if (isEdit && category) await financeService.updateCategory(category.id, input);
      else await financeService.createCategory(input);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Category updated' : 'Category created');
      void queryClient.invalidateQueries({ queryKey: ['finance', 'categories'] });
      void queryClient.invalidateQueries({ queryKey: ['finance', 'budget'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Category' : 'Add Category'}</DialogTitle>
          <DialogDescription>Configure purchase category</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="c-name">Name *</Label>
            <Input id="c-name" {...register('name', { required: 'Name is required' })} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-budget">Monthly Budget (₹)</Label>
            <Input id="c-budget" type="number" min="0" step="0.01" {...register('monthly_budget')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-max">Max Purchase Limit (₹)</Label>
            <Input id="c-max" type="number" min="0" step="0.01" {...register('max_purchase_limit')} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...register('gst_applicable')} className="h-4 w-4 rounded border-input" />
            GST Applicable
          </label>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
